/**
 * TABLA DE MATERIALES — la misma para riego y para eléctrico.
 *
 * Existe desde el 2026-08-14, cuando el catálogo de Juan quedó conectado a los motores de
 * ingeniería. Antes cada módulo pintaba su propia tabla y ninguna mostraba precio, porque
 * no había de dónde sacarlo.
 *
 * Lo que esta tabla tiene que dejar claro, y por qué:
 *
 * 1. **Las dos justificaciones van separadas**, porque se corrigen en lugares distintos.
 *    Si la CANTIDAD está mal, se arregla midiendo otra vez o moviendo un punto del plano.
 *    Si el PRODUCTO está mal, se arregla en Configuración. Mezclarlas obliga a adivinar
 *    dónde se toca.
 * 2. **Un insumo que falta se ve, y se ve distinto.** No como un renglón de $0 —que se lee
 *    como «es gratis»— sino como un pendiente con su cantidad ya calculada.
 * 3. **El total no incluye lo que falta.** Un total que suma renglones sin precio miente
 *    hacia abajo, que es la peor dirección para una cotización.
 */

const mx = n => '$' + Math.round(n || 0).toLocaleString('es-MX')

export default function Materiales ({ lista, auditando }) {
  if (!lista || !lista.length) return null

  const faltan = lista.filter(m => m.falta)
  const total = lista.filter(m => !m.falta).reduce((s, m) => s + (m.costo || 0), 0)

  return (
    <>
      <table className="d">
        <thead>
          <tr>
            <th>Material</th>
            <th className="n">Cantidad</th>
            <th>Unidad</th>
            <th className="n">Costo</th>
          </tr>
        </thead>
        <tbody>
          {lista.map((m, n) => (
            <tr key={n} className={m.falta ? 'pendiente' : undefined}>
              <td>
                {m.concepto}
                {/* La razón técnica: por qué esta cantidad. Sale del plano, no del catálogo. */}
                {auditando && m.de && <div className="s">{m.de}</div>}
                {/* La razón comercial: por qué este producto. Sale del catálogo de Juan. */}
                {auditando && m.porque && <div className="s">{m.porque}</div>}
              </td>
              <td className="n">{m.cantidad}</td>
              <td>{m.unidad}</td>
              <td className="n">
                {m.falta
                  ? <span className="s">sin precio</span>
                  : <>{mx(m.costo)}{auditando && m.costoUnitario != null &&
                      <div className="s">{mx(m.costoUnitario)} c/u</div>}</>}
              </td>
            </tr>
          ))}
        </tbody>
        <tfoot>
          <tr>
            <td colSpan="3"><b>Costo de material</b></td>
            <td className="n"><b>{mx(total)}</b></td>
          </tr>
        </tfoot>
      </table>

      {/* El aviso NO es decorativo: sin él, el total de arriba se lee como el costo real
          y va a ser menor de lo que va a costar. Se dice qué falta y dónde se arregla. */}
      {faltan.length > 0 && (
        <div className="warn leible" style={{ marginTop: 'var(--e4)' }}>
          <b>{faltan.length === 1
            ? 'Falta un insumo en tu catálogo.'
            : `Faltan ${faltan.length} insumos en tu catálogo.`}</b>{' '}
          El diseño está completo y las cantidades son correctas, pero estos renglones no
          tienen con qué cobrarse, así que <b>no están sumados en el total</b>:
          <ul style={{ margin: 'var(--e3) 0 0', paddingLeft: '1.1rem' }}>
            {faltan.map((m, n) => (
              <li key={n}>{m.concepto.replace('Falta dar de alta: ', '')} — {m.cantidad} {m.unidad}</li>
            ))}
          </ul>
          <p style={{ margin: 'var(--e3) 0 0' }}>
            Se dan de alta en <b>Configuración → Insumos</b>, y a partir de ahí cualquier
            levantamiento los encuentra solo.
          </p>
        </div>
      )}
    </>
  )
}
